<template>
  <v-dialog v-model="localValue" max-width="548" max-height="460" persistent>
    <v-card class="qr-modal">
      <v-card-title class="qr-modal__header">
        <h2 class="qr-modal__title">QR Code</h2>
        <v-btn @click="closeModal" icon class="qr-modal__close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text class="qr-modal__content">
        <p class="qr-modal__subtitle">
          Share your form effortlessly with a downloadable QR code.
        </p>
        
        <div class="qr-modal__qr-container">
          <div v-if="qrLoading" class="qr-modal__loading">
            <v-progress-circular 
              size="120" 
              width="2" 
              color="blue_400" 
              indeterminate
            ></v-progress-circular>
          </div>
          <img 
            v-else-if="!qrError && qrDataUrl"
            :src="qrDataUrl"
            alt="QR Code for form link"
            class="qr-modal__qr-image"
          />
          <div v-else-if="qrError" class="qr-modal__error">
            <small class="text--secondary">QR code unavailable</small>
            <br>
            <small class="text--secondary">{{ errorMessage }}</small>
          </div>
        </div>
      </v-card-text>
      
      <v-card-actions class="qr-modal__actions">
        <v-btn 
          @click="copyQRCode" 
          outlined 
          class="qr-modal__copy-btn"
          :disabled="qrError || qrLoading || !qrDataUrl"
        >
          {{ copied ? 'Copied!' : 'Copy' }}
        </v-btn>
        <v-btn 
          @click="downloadQRCode" 
          color="blue_400" 
          class="qr-modal__download-btn"
          :disabled="qrError || qrLoading || !qrDataUrl"
        >
          Download
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'QRCodeModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    shareLink: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      qrLoading: false,
      qrError: false,
      qrDataUrl: '',
      copied: false,
      errorMessage: ''
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.shareLink) {
        console.log('Modal opened, generating QR for:', this.shareLink)
        this.generateQRCode()
      }
      if (!newVal) {
        this.resetState()
      }
    },
    shareLink(newLink) {
      if (newLink && this.value) {
        console.log('Share link changed:', newLink)
        this.generateQRCode()
      }
    }
  },
  methods: {
    async generateQRCode() {
      if (!this.shareLink) {
        console.error('No share link provided')
        this.qrError = true
        this.errorMessage = 'No share link provided'
        return
      }
      
      try {
        this.qrLoading = true
        this.qrError = false
        this.qrDataUrl = ''
        this.errorMessage = ''
        
        console.log('Generating QR code for URL:', this.shareLink)
        
        // Generate QR code as data URL
        const qrDataUrl = await QRCode.toDataURL(this.shareLink, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M'
        })
        
        console.log('QR code generated successfully')
        this.qrDataUrl = qrDataUrl
        this.qrLoading = false
      } catch (error) {
        console.error('Error generating QR code:', error)
        this.qrError = true
        this.qrLoading = false
        this.errorMessage = error.message || 'Failed to generate QR code'
      }
    },
    
    async copyQRCode() {
      if (!this.qrDataUrl || this.qrError || this.qrLoading) return
      
      try {
        // Convert data URL to blob
        const response = await fetch(this.qrDataUrl)
        const blob = await response.blob()
        
        try {
          const item = new ClipboardItem({ 'image/png': blob })
          await navigator.clipboard.write([item])
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 2000)
        } catch (clipboardError) {
          console.log('Image copy failed, copying link as text:', clipboardError)
          // Fallback: copy the link as text
          await navigator.clipboard.writeText(this.shareLink)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 2000)
        }
      } catch (error) {
        console.error('Failed to copy:', error)
        // Final fallback: copy text
        try {
          await navigator.clipboard.writeText(this.shareLink)
          this.copied = true
          setTimeout(() => {
            this.copied = false
          }, 2000)
        } catch (e) {
          console.error('All copy methods failed:', e)
        }
      }
    },
    
    downloadQRCode() {
      if (!this.qrDataUrl || this.qrError || this.qrLoading) return
      
      try {
        const link = document.createElement('a')
        link.download = 'form-qr-code.png'
        link.href = this.qrDataUrl
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error downloading QR code:', error)
      }
    },
    
    closeModal() {
      this.localValue = false
    },
    
    resetState() {
      this.copied = false
      this.qrError = false
      this.qrLoading = false
      this.qrDataUrl = ''
      this.errorMessage = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.qr-modal {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 16px 24px;
    margin: 0 !important;
  }
  
  &__title {
    font-size: 20px;
    font-weight: 600;
    color: #101828;
    margin: 0;
  }
  
  &__close-btn {
    color: #667085 !important;
  }
  
  &__content {
    padding: 0 24px 24px 24px;
    text-align: center;
  }
  
  &__subtitle {
    margin-top: -10px;
    font-size: 14px;
    text-align: left;
    color: #667085;
    margin-bottom: 32px;
    line-height: 1.5;
  }
  
  &__qr-container {
    margin-top: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
  }
  
  &__qr-image {
    width: 264px;
    height: 258px;
  }
  
  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  &__error {
    color: #667085;
    text-align: center;
  }
  
  &__actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }
  
  &__copy-btn {
    min-width: 100px;
    padding: 8px 32px !important;
    height: 44px;
    border-color: #2F80ED !important;
    color: #2F80ED !important;
    font-weight: 500;
    border-radius: 26px;

    &:hover {
      background-color: #F9FAFB !important;
    }
  }
  
  &__download-btn {
    min-width: 120px;
    height: 44px;
    font-weight: 500;
    border-radius: 26px;
    padding: 8px 32px !important;
    
    &:hover {
      background-color: #2F80ED !important;
    }
  }
}
</style> 